import * as Sentry from "@sentry/react";
import {
  TRANSPORT_BREADCRUMB_CATEGORY,
  TRANSPORT_DROP_TAG,
  isTransportDrop,
} from "@/lib/transportDrop";

/**
 * One place that owns the order of operations when a failure is both logged
 * and reported. Issue #1255.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * `8c0b454` added `captureConsoleIntegration({ levels: ["error"] })` to
 * `main.tsx`. That was right — it closed a blind spot where a caught
 * PostgREST error logged to the console produced no Sentry event at all, and
 * #1210 ran broken for seven weeks because of it.
 *
 * But it means every `console.error` is now itself a Sentry event, so the
 * common shape
 *
 *     console.error("[X] failed", err);        // ← event 1, fires FIRST
 *     Sentry.captureException(err, { tags });  // ← event 2
 *
 * produces TWO events from one failure, and **the first one is the one that
 * reaches #bugs**. Two things go wrong with it:
 *
 *   1. **It has no tags.** Per-call `tags`/`extra` — the second argument to
 *      `captureException` — apply to that one capture. The console line fires
 *      before any of it is live, so the console event carries none of it.
 *      (Client options set in `Sentry.init`, like `environment` and `release`,
 *      DO reach both events — they are applied to every event. So a fix routed
 *      through `init` is safe; a fix passed per-call is not.)
 *   2. **It titles as `[object Object]`.** `console.error(msg, err)` with a
 *      plain PostgrestError stringifies the object into the message. Sentry
 *      groups and titles on that, so distinct failures collapse into one issue
 *      and the message that would tell them apart is the part that was
 *      stringified away.
 *
 * ── What this does about it ────────────────────────────────────────────────
 *
 * Both fixes from #1255, together, in the one order that works:
 *
 *   - **Scope first, then log.** The tags are live *before* `console.error`
 *     runs, so the console-path event inherits them.
 *   - **Log a string, not an object.** `describeCause` flattens a
 *     PostgrestError to `code message`, so the title says what failed.
 *
 * ⚠️ The order is the whole point. Logging before `scope.setTags` puts the
 * tags on the wrong event and the failure is invisible — both events still
 * arrive, one just quietly has nothing on it. `reportErrorOrdering.test.ts`
 * pins it against a real Sentry client rather than a mock.
 */

/** A PostgrestError is a plain object, not an Error — `String(e)` is useless. */
export function describeCause(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  if (cause && typeof cause === "object") {
    const o = cause as { code?: unknown; message?: unknown };
    const msg = typeof o.message === "string" ? o.message : "";
    const code = typeof o.code === "string" ? o.code : "";
    if (msg) return code ? `${code} ${msg}` : msg;
    // Last resort: keys, which is at least greppable. Never "[object Object]".
    return `non-error object with keys: ${Object.keys(o).join(", ") || "(none)"}`;
  }
  return String(cause);
}

/**
 * A PostgrestError is not an Error, and Sentry titles those "Object captured
 * as exception with keys: …" — unreadable and ungroupable. Wrap it, carrying
 * the structured fields across so triage keeps `code`/`details`/`hint`.
 *
 * Exported so it can be asserted directly. `Sentry.captureException` cannot be
 * spied on — ESM module namespaces are not configurable — so testing this
 * through the mock would not have been possible anyway.
 */
export function toReportableError(message: string, cause: unknown): Error {
  if (cause instanceof Error) return cause;
  const c = cause as { code?: unknown; details?: unknown; hint?: unknown } | null;
  return Object.assign(new Error(`${message}: ${describeCause(cause)}`), {
    code: c?.code,
    details: c?.details,
    hint: c?.hint,
  });
}

export interface ReportContext {
  /** Indexed and filterable in Sentry. Keep values short and low-cardinality. */
  tags?: Record<string, string>;
  /** Anything useful for triage. Not indexed. */
  extra?: Record<string, unknown>;
}

/**
 * Log a failure to the console AND report it to Sentry, exactly once each,
 * with the tags on both.
 *
 * @param message  What failed, in the shape used as the Sentry title.
 * @param cause    The thrown value or PostgrestError.
 */
export function reportError(message: string, cause: unknown, context: ReportContext = {}): void {
  const described = describeCause(cause);

  // ── 0. A dropped connection is not a defect. #1811. Nothing in the app
  //    failed and nothing a mom did is recoverable by paging us, so it is
  //    recorded and not captured. See `transportDrop.ts` for the three shapes
  //    and for why the console tap has to be handled too.
  if (isTransportDrop(cause)) {
    // Recorded instead of captured, so the drops leading up to a real error
    // are sitting on that event when someone triages it. Asserted by reading
    // it off a LATER event, which is the only thing that makes "recorded"
    // worth more than deleting it.
    //
    // ⚠️ The placement — outside the `withScope` below — is READABILITY, not
    // mechanism, and an earlier version of this comment claimed otherwise.
    // `Sentry.addBreadcrumb` writes to `getIsolationScope()` unconditionally
    // (`@sentry/core/build/cjs/breadcrumbs.js:21`), so it would survive inside
    // the scope too; moving it in was mutation-tested and changed nothing.
    // Do not "fix" a future refactor by relying on the scope here.
    Sentry.addBreadcrumb({
      category: TRANSPORT_BREADCRUMB_CATEGORY,
      level: "info",
      message: `${message}: ${described}`,
      data: { ...context.tags },
    });

    // Console output is unchanged — a drop still prints for a Web Inspector
    // attached to her phone. But the event `captureConsoleIntegration`
    // synthesises from this line would page on its own (it is the one that
    // reaches #bugs first, per #1255), so the scope is marked BEFORE the log
    // and `isTransportDropEvent` drops it in `beforeSend`. The ordering is
    // load-bearing for the same reason it is below.
    Sentry.withScope((scope) => {
      if (context.tags) scope.setTags(context.tags);
      if (context.extra) scope.setExtras(context.extra);
      scope.setTag(TRANSPORT_DROP_TAG, "true");
      console.error(`${message}: ${described}`);
    });
    return;
  }

  Sentry.withScope((scope) => {
    // ── 1. Scope FIRST. Anything after this inherits it, including the event
    //    captureConsoleIntegration synthesises from the console.error below.
    if (context.tags) scope.setTags(context.tags);
    if (context.extra) scope.setExtras(context.extra);

    // ── 2. Then log — as a single interpolated string. Passing `cause` as a
    //    second argument is what produced "[object Object]" titles.
    console.error(`${message}: ${described}`);

    // ── 3. Then capture explicitly. A PostgrestError is not an Error, and
    //    Sentry titles those "Object captured as exception with keys: …",
    //    which is unreadable and ungroupable — so wrap it, carrying the
    //    structured fields across for triage.
    // The context is ALSO passed explicitly, not only left on the scope.
    // Redundant for this event — the scope already carries it — but it keeps
    // the captureException path's contract byte-identical to what it was
    // before #1255, so nothing downstream that reads the per-call context
    // changes behaviour. The scope is what fixes the CONSOLE event.
    Sentry.captureException(toReportableError(message, cause), {
      tags: context.tags,
      extra: context.extra,
    });
  });
}
